import { useEffect, useMemo } from "react";

import type { EffectiveSummary } from "@/modules/dashboard/settings/types/access.types";
import { useOrganizationDirectory, type DirectoryEntry } from "@/shared/hooks/useOrganizationDirectory";
import useTranslations from "@/shared/lang/useTranslations";

import type { UserRowActionsProps } from "../components/UserRowActions";
import type { UserStatus } from "../types/create-user.types";
import type { UserAssignedRole, UserListItem } from "../types/users-api.types";
import { resolveOrganizationCell, type OrganizationCellView } from "../utils/organizationCell";
import { resolveSignInHealth, type SignInHealth } from "../utils/signInHealth";
import { resolveStatusPresentation, type StatusVariant } from "../utils/statusPresentation";
import type { UsersAccessState } from "./useUsersAccess";

export interface UserRowView {
  user: UserListItem;
  isCurrentUser: boolean;
  organization: OrganizationCellView;
  isOrganizationPending: boolean;
  roles: readonly UserAssignedRole[];
  summary: EffectiveSummary | undefined;
  rolesUnavailable: boolean;
  health: SignInHealth;
  statusVariant: StatusVariant;
  statusLabel: string;
  onOpenDetail: () => void;
  onManageAccess: () => void;
  actions: UserRowActionsProps;
}

export interface UserRowViewsInput {
  rows: readonly UserListItem[];
  access: UsersAccessState;
  currentUserId: string | null;
  showOrganization: boolean;
  canWrite: boolean;
  canDelete: boolean;
  canManageRoles: boolean;
  onOpenDetail: (user: UserListItem) => void;
  onManageAccess: (user: UserListItem) => void;
  onVerifyEmail: (user: UserListItem) => void;
  onSetStatus: (user: UserListItem, status: UserStatus) => void;
  onSetPassword: (user: UserListItem) => void;
  onDelete: (user: UserListItem) => void;
}

export interface UserRowViewsResult {
  views: UserRowView[];
  isDirectoryPending: boolean;
  directoryFailed: boolean;
  directoryTruncated: boolean;
  /** Re-exposed so a drawer can name the same organization without a second subscription. */
  lookupOrganization: (id: string | null | undefined) => DirectoryEntry | undefined;
}

/**
 * Turns a page of API rows into everything the table and the cards need, computed ONCE per data change.
 *
 * This is where the organization column's performance lives. The page subscribes to the directory a single
 * time and hands each cell an already-resolved view, instead of twenty-five cells each subscribing and each
 * looking up. The memo is keyed on the row array plus the directory `version`, so a name that arrives late
 * still lands — and nothing recomputes on an unrelated render.
 *
 * The one thing deliberately NOT done here: storing the result in state. A positional array of names would
 * let a slow organization response paint page N's names into page N+1's rows. Deriving from a keyed store,
 * memoised on `rows`, makes that impossible — a late response can only ever add a correct `id → name` pair.
 */
export function useUserRowViews({
  rows,
  access,
  currentUserId,
  showOrganization,
  canWrite,
  canDelete,
  canManageRoles,
  onOpenDetail,
  onManageAccess,
  onVerifyEmail,
  onSetStatus,
  onSetPassword,
  onDelete,
}: UserRowViewsInput): UserRowViewsResult {
  const { t } = useTranslations("users");
  const directory = useOrganizationDirectory(showOrganization);
  const { ensure, lookup, isPending } = directory;
  // Destructured so the memo below can depend on the individual members. Reading them off `access` inside it
  // makes the whole object a dependency, and `access` is a memo that moves for reasons the rows do not care
  // about.
  const { rolesById, summaryFor, rolesUnavailable } = access;

  // Ask for the ids this page needs. Deduplicated inside the directory, so a page where every row shares one
  // organization costs one request, not twenty-five. `ensure` changes identity with the store's version, so
  // this re-runs once the preload settles and the still-unresolved ids can fall through to the fallback.
  useEffect(() => {
    if (!showOrganization) return;
    ensure(rows.map((row) => row.organization_id));
  }, [rows, ensure, showOrganization]);

  const views = useMemo(
    () =>
      rows.map<UserRowView>((user) => {
        const isCurrentUser = user.id === currentUserId;
        const roles = rolesById[user.id] ?? [];
        const status = resolveStatusPresentation(user.status, t);

        const openDetail = () => onOpenDetail(user);
        const manageAccess = () => onManageAccess(user);

        return {
          user,
          isCurrentUser,
          organization: resolveOrganizationCell(user.organization_id, lookup(user.organization_id)),
          isOrganizationPending: isPending(user.organization_id),
          roles,
          // `summaryFor` owns the "no tally until the catalog is loaded" rule — see `useUsersAccess`. It used
          // to be applied here and nowhere else, so the detail drawer printed `0/84` while the row behind it
          // correctly printed nothing.
          summary: summaryFor(user.id),
          rolesUnavailable,
          health: resolveSignInHealth(user),
          statusVariant: status.variant,
          statusLabel: status.label,
          onOpenDetail: openDetail,
          onManageAccess: manageAccess,
          actions: {
            user,
            isCurrentUser,
            canWrite,
            canDelete,
            canManageRoles,
            onOpenDetail: openDetail,
            onManageAccess: manageAccess,
            onVerifyEmail: () => onVerifyEmail(user),
            onSetStatus: (status) => onSetStatus(user, status),
            onSetPassword: () => onSetPassword(user),
            onDelete: () => onDelete(user),
          },
        };
      }),
    [
      rows,
      rolesById,
      summaryFor,
      rolesUnavailable,
      currentUserId,
      canWrite,
      canDelete,
      canManageRoles,
      lookup,
      isPending,
      t,
      onOpenDetail,
      onManageAccess,
      onVerifyEmail,
      onSetStatus,
      onSetPassword,
      onDelete,
    ],
  );

  return {
    views,
    isDirectoryPending: directory.isPreloading,
    directoryFailed: directory.hasError || directory.accessDenied,
    directoryTruncated: directory.isTruncated,
    lookupOrganization: lookup,
  };
}
